//
// # AlphaClient
//
// Browser-side HTTP client for the IDE.
// Communicates with AlphaServer via HTTP POST requests.
// Also manages a WebSocket connection for streaming progress during prompt processing.
//

import typia from "typia";
import {
  Context,
  ContextWire,
  LoadKind,
  Result as CoreResult,
  Ok,
  ReconnectionManager,
  validate as v,
} from "CoreUtils";
import { schema } from "GenAlphaSchema";
import { model } from "GenAlphaModel";
import {
  OpenHierarchyResult,
  type ImageAttachment,
  type Payload,
  type Prompt,
} from "ServerInterface";
import type { AgentTreeNode } from "AgentRunTreeSchema";
import type { LvsError, LvsStats } from "./components/LvsPanel/types";
import type { DrcError, DrcStats } from "./components/DrcPanel/types";
import type { IvaError, IvaStats } from "./components/IvaPanel/types";
import {
  createRequestContext,
  setSessionId,
  setUserEmail,
} from "./contextHelpers";
import { logWithGcpUrlFiltered } from "./gcp";

// Local shorthands
type Result<T> = CoreResult<T, { why: string; data?: unknown }>;
type AResult<T> = Promise<Result<T>>;
const Result = CoreResult;

// Result-wrapped fetch
const f = Result.wrapAsync(fetch);

// Discriminates a streamed frame: `answer` is the conversation root's
// terminal output (rendered as THE answer, once); `progress` is whole-tree
// activity (every agent's interim progress),
// rendered as transient activity rather than a chat message.
export type ProgressKind = "progress" | "answer";

// Progress message sent by AlphaServer over WebSocket during prompt processing.
// Routed to the matching conversation's transcript by `conversationId`:
// the IDE holds one persistent WS subscription and dispatches by conversationId,
// so the frame carries no per-prompt requestId.
interface ProgressMessage {
  type: "progress";
  conversationId: string;
  message: string;
  kind: ProgressKind;
  timestamp: string;
}
const ProgressMessageValidator = {
  validator: new v.Validator(typia.createValidate<ProgressMessage>()),
};

// Agent-tree snapshot pushed by AlphaServer whenever the running-agent
// hierarchy changes (debounced server-side). `tree` is null once the
// conversation's tree is gone. Carries clientId for routing; the tree
// self-identifies its conversation via its root agentId.
interface AgentTreeMessage {
  type: "agent-tree";
  clientId: string;
  tree: AgentTreeNode | null;
  timestamp: string;
}
const AgentTreeMessageValidator = {
  validator: new v.Validator(typia.createValidate<AgentTreeMessage>()),
};

// Design-load event sent by AlphaServer when state changes are applied.
// The ident field is an opaque schema.Ident — we pass it through without
// fully validating its inner structure.
export interface DesignLoadMessage {
  kind: "design-load";
  ident: unknown;
  contentKind: string;
  loadKind: LoadKind;
  savedIdents?: unknown[]; // Sub-designs whose change triggered this refresh, for targeted cache invalidation
  // Extra fields added by server for logging/context - optional to allow validation
  context?: unknown;
  timestamp?: string;
}
const DesignLoadMessageValidator = {
  validator: new v.Validator(typia.createValidate<DesignLoadMessage>()),
};

export interface DesignChangesMessage {
  kind: "changes";
  ident: schema.Ident;
  contentKind: schema.ContentKind;
  changes: model.Change[];
  context?: ContextWire;
}
const DesignChangesMessageValidator = {
  validator: new v.Validator(typia.createValidate<DesignChangesMessage>()),
};

// Design-open event sent by AlphaServer when the LM opens a design
// (e.g. depot open tool). The IDE should open a new tab for it.
export interface DesignOpenMessage {
  kind: "design-open";
  ident: unknown;
  dirty?: boolean;
}
const DesignOpenMessageValidator = {
  validator: new v.Validator(typia.createValidate<DesignOpenMessage>()),
};

// LVS result event sent by AlphaServer when LVS verification completes.
// The IDE uses this to populate the LVS panel with errors/results.
export interface LvsResultMessage {
  kind: "lvs-result";
  ident: unknown;
  result: {
    passed?: boolean;
    errors?: LvsError[];
    stats?: LvsStats;
  };
}
const LvsResultMessageValidator = {
  validator: new v.Validator(typia.createValidate<LvsResultMessage>()),
};

// DRC result event sent by AlphaServer when DRC verification completes.
// The IDE uses this to populate the DRC panel with errors/results.
export interface DrcResultMessage {
  kind: "drc-result";
  ident: unknown;
  result: {
    passed?: boolean;
    errors?: DrcError[];
    stats?: DrcStats;
  };
}
const DrcResultMessageValidator = {
  validator: new v.Validator(typia.createValidate<DrcResultMessage>()),
};

// IVA result event sent by AlphaServer when IVA verification completes.
// The IDE uses this to populate the IVA panel with errors/results.
export interface IvaResultMessage {
  kind: "iva-result";
  ident: unknown;
  result: {
    passed: boolean;
    errors: IvaError[];
    stats: IvaStats;
  };
}
const IvaResultMessageValidator = {
  validator: new v.Validator(typia.createValidate<IvaResultMessage>()),
};

// SRV result event sent by AlphaServer when Schematic Rules Validation
// completes. The IDE uses this to populate the SRV panel and canvas overlay.
// The `result` shape structurally matches `srv.SrvRunResult` (GenAlphaModel),
// so it can be handed straight to `SrvModel.applySrvResult`.
export interface SrvResultMessage {
  kind: "srv-result";
  ident: unknown;
  result: {
    ranRuleIds: string[];
    violations: Array<{
      ruleId: string;
      message: string;
      points: Array<{ x: number; y: number }>;
      wireSegmentIds: number[];
      schPinIds: number[];
      instanceIds: number[];
    }>;
    stats: Array<{
      ruleId: string;
      violationCount: number;
      elapsedMs: number;
    }>;
  };
}
const SrvResultMessageValidator = {
  validator: new v.Validator(typia.createValidate<SrvResultMessage>()),
};

// Validator for a single Ident -- used at data boundaries for WS events.
export const IdentValidator = {
  validator: new v.Validator(typia.createValidate<schema.Ident>()),
};

// Canvas capture request sent by AlphaServer when the Copilot's get-img tool
// needs a screenshot of the editor canvas.
interface CaptureCanvasRequest {
  kind: "capture-canvas-request";
  uuid: string;
  context: unknown;
}
const CaptureCanvasRequestValidator = {
  validator: new v.Validator(typia.createValidate<CaptureCanvasRequest>()),
};

// Ack sent by AlphaServer after a successful WebSocket registration. The
// serverInstanceId is unique per server process and lets the client detect a
// server restart on reconnect. clientKnown reflects whether the server still
// has a ClientContext for this clientId (false after a restart, even if the
// server is the same identity).
interface RegisterWebSocketAck {
  kind: "register-websocket-ack";
  serverInstanceId: string;
  clientKnown: boolean;
}
const RegisterWebSocketAckValidator = {
  validator: new v.Validator(typia.createValidate<RegisterWebSocketAck>()),
};

// App-level pong reply sent by the server in response to a client ping. The
// heartbeat carries no payload; only the discriminator is used.
interface PongMessage {
  kind: "pong";
}
const PongMessageValidator = {
  validator: new v.Validator(typia.createValidate<PongMessage>()),
};
// Per-call timeout and retry configuration for AlphaServer requests.
// Default: single attempt with 10s timeout (no retries).
interface CallOptions {
  maxAttempts: number;
  timeoutMs: number;
  retryDelayMs: number;
}

const DEFAULT_CALL_OPTIONS: CallOptions = {
  maxAttempts: 1,
  timeoutMs: 10_000,
  retryDelayMs: 0,
};

export interface AlphaClientArgs {
  alphaServerUrl: string;
  // Returns a fresh Firebase ID token for authenticated requests.
  // Called before each API request so the token is always valid.
  getToken?: () => Promise<string | null>;
  // User email from Firebase auth (for logging/context)
  userEmail?: string;
}

// Progress callback type. `kind` lets the chat render the root's `answer` as a
// persistent message and `progress` as transient whole-tree activity.
export type ProgressCallback = (message: string, kind: ProgressKind) => void;

// Reasons we surface a hard "connection lost — reload?" condition to the UI.
// Each maps to a distinct user-facing message in ReconnectDialog:
//   - "retries-exhausted": auto-reconnect gave up; server may be unreachable.
//   - "server-restarted":  ack returned a new serverInstanceId; server state
//                          is gone, our session is invalid.
//   - "client-unknown":    server is the same instance but doesn't know our
//                          clientId (e.g. evicted, or initial register raced).
export type ConnectionLostReason =
  | "retries-exhausted"
  | "server-restarted"
  | "client-unknown";

// A discriminated union of WebSocket lifecycle events emitted by AlphaClient.
// Consumers (main.tsx) dispatch on `kind` to update UI state and log entries.
// The simpler onConnectionStateChanged callback below still exists for the
// passive status indicator; onConnectionEvent carries the richer context
// (close codes, downtime, attempt counts) needed for the console log.
export type ConnectionEvent =
  | {
      readonly kind: "connected";
      readonly clientId: string;
      readonly url: string;
    }
  | {
      readonly kind: "disconnected";
      readonly clientId: string;
      // Close code per the WebSocket spec (e.g. 1006 abnormal closure,
      // 4000+ for application-defined codes we use for forced reconnects).
      readonly code: number;
      readonly reason: string;
      // Whether the connection lasted long enough for ReconnectionManager to
      // reset the attempt counter (i.e. the connection was "useful").
      readonly wasStable: boolean;
      readonly durationMs: number;
    }
  | {
      readonly kind: "reconnected";
      readonly clientId: string;
      readonly url: string;
      // Time between the previous onclose and this onopen. Measured at the
      // client, so it includes any backoff delay.
      readonly downtimeMs: number;
      // Number of reconnection attempts made during this outage (before the
      // manager is reset on open).
      readonly attempts: number;
    };

export class AlphaClient {
  private readonly _clientId: string;

  // WebSocket for receiving streaming progress messages from AlphaServer
  private ws: WebSocket | null = null;
  private wsReconnecting: boolean = false;
  private wsReconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private wsDisposed: boolean = false;
  private readonly wsReconnectManager = new ReconnectionManager();

  // Set on the first successful WebSocket open. Used to distinguish the
  // initial "connected" event from subsequent "reconnected" events.
  private wsEverConnected: boolean = false;

  // Timestamp (Date.now()) of the most recent onclose, or null if we have
  // never been connected or are currently open. Used to compute downtimeMs
  // for the "reconnected" event.
  private wsLastDisconnectedAt: number | null = null;

  // Remembered serverInstanceId from the first successful register ack. On
  // subsequent reconnects we compare the new ack's id against this; a mismatch
  // means the server process restarted and our server-side state is gone.
  private knownServerInstanceId: string | null = null;

  // Maximum reconnect attempts before we give up and surface the dialog.
  // Combined with ReconnectionManager's 2s/4s/8s backoff this means we wait
  // ~14s before declaring the connection lost — long enough to ride out a
  // typical wifi blip, short enough not to feel frozen.
  private static readonly MAX_RECONNECT_ATTEMPTS_BEFORE_DIALOG = 3;

  // Set true after we've fired onConnectionLost so we don't fire it twice
  // and don't keep scheduling reconnects. Cleared by dispose().
  private connectionLost: boolean = false;

  // Guards the silent same-instance recovery path (recoverAfterClientUnknown)
  // so overlapping register-acks don't kick off concurrent re-registrations.
  private recovering: boolean = false;

  // App-level ping/pong heartbeat state.
  //
  // Background: the browser's WebSocket API does not expose protocol-level
  // ping/pong frames to JavaScript, so we cannot use the server's built-in
  // heartbeat to detect dead connections on the client side. Instead we send
  // a JSON `{kind:"ping"}` every pingIntervalMs; the server replies with a
  // `{kind:"pong"}`. If we don't hear back within pongTimeoutMs we assume the
  // connection is a zombie (e.g. after machine sleep/wake) and force a close,
  // which triggers the existing reconnect path.
  private static readonly PING_INTERVAL_MS = 20_000;
  private static readonly PONG_TIMEOUT_MS = 10_000;
  // Shorter deadline used when a visibilitychange / online event fires — we
  // want to find out fast whether the socket is still alive.
  private static readonly PROBE_PONG_TIMEOUT_MS = 5_000;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private pongTimeout: ReturnType<typeof setTimeout> | null = null;

  // Count of in-flight long operations during which the heartbeat is
  // suppressed (a busy peer can't pong within the deadline). Counter, not a
  // bool, so concurrent ops compose. See beginBusy / endBusy.
  private inFlightOps = 0;

  // Guard so we only attach document/window listeners once, even if
  // connectWebSocket() is called multiple times due to reconnects.
  private connectivityListenersAttached: boolean = false;

  // Per-conversation progress sinks, keyed by conversationId.
  //
  // The IDE holds ONE persistent WS subscription (set up once via
  // subscribeConversation, lives for the connection) and routes each progress /
  // streamed-answer frame to the matching conversation's sink by conversationId.
  // This replaces the old per-requestId register-before / delete-after-per-prompt
  // lifecycle: requestId is not used for routing, and a prompt's ack
  // (which returns in ~20ms, long before the agent streams its answer)
  // does NOT tear the subscription down. Keyed by conversationId so it supports
  // more than one concurrent conversation — groundwork for parallel / prefetched
  // conversations; the IDE drives a single conversation today.
  private readonly conversationSinks = new Map<string, ProgressCallback>();

  // Abort controllers keyed by requestId.
  // Used to cancel in-flight prompt requests when the user clicks Stop.
  private readonly abortControllers = new Map<string, AbortController>();

  // Callback for design-load events from AlphaServer.
  // Set by Workspace to reload editor content when state changes are applied.
  onDesignLoad: ((msg: DesignLoadMessage) => void) | null = null;

  // Callback for incremental design-changes events from AlphaServer.
  // Set by Workspace to apply streamed changes live (no full reload).
  onDesignChanges: ((msg: DesignChangesMessage) => void) | null = null;

  // Callback for design-open events from AlphaServer.
  // Set by Workspace to open a new tab when the LM opens a design.
  onDesignOpen: ((msg: DesignOpenMessage) => void) | null = null;

  // Callback for LVS result events from AlphaServer.
  // Set by Workspace to populate the LVS panel with verification results.
  onLvsResult: ((msg: LvsResultMessage) => void) | null = null;

  // Callback for agent-tree pushes from AlphaServer.
  // Set by Workspace to refresh the Agents panel with the live hierarchy.
  onAgentTree: ((msg: AgentTreeMessage) => void) | null = null;

  // Callback for DRC result events from AlphaServer.
  // Set by Workspace to populate the DRC panel with verification results.
  onDrcResult: ((msg: DrcResultMessage) => void) | null = null;

  // Callback for IVA result events from AlphaServer.
  // Set by Workspace to populate the IVA panel with verification results.
  onIvaResult: ((msg: IvaResultMessage) => void) | null = null;

  // Callback for SRV result events from AlphaServer.
  // Set by main.tsx to apply Schematic Rules Validation results to the SrvModel.
  onSrvResult: ((msg: SrvResultMessage) => void) | null = null;

  // Callback for canvas capture requests from AlphaServer.
  // Set by Workspace to capture the active editor's canvas for the get-img tool.
  // Returns base64 PNG data or an error string.
  onCaptureCanvas: (() => CoreResult<string, string>) | null = null;

  // Callback for depot tree change events (design-saved, design-added, design-removed).
  // Set by Workspace to trigger DesignExplorer re-fetch.
  // The kind is passed so Workspace can decide whether the tree actually changed.
  onDesignTreeChanged:
    | ((kind: "design-saved" | "design-added" | "design-removed") => void)
    | null = null;

  // Fired on WebSocket open (true) and close (false). Set by Workspace to
  // propagate the connection state into the UI snapshot for the network
  // status indicator. Coarse boolean; see onConnectionEvent for details.
  onConnectionStateChanged: ((isConnected: boolean) => void) | null = null;

  // Fired for the richer connected / disconnected / reconnected lifecycle,
  // carrying close codes, downtime, and attempt counts. Used by main.tsx to
  // write entries to the in-app console panel (see Workspace.logToConsole).
  onConnectionEvent: ((event: ConnectionEvent) => void) | null = null;

  // Fired once when we determine the WebSocket connection cannot be recovered
  // in-place — either retries were exhausted, the server restarted (new
  // serverInstanceId), or the server no longer recognises our clientId. The
  // UI is expected to show a modal prompting the user to reload the page.
  onConnectionLost: ((reason: ConnectionLostReason) => void) | null = null;

  // Fired after a transient disconnect where the same server instance dropped
  // our ClientContext (see handleRegisterAck). We've silently re-registered;
  // the app should re-open its open designs so the server's fresh StateManager
  // re-adopts the still-alive (grace-windowed) shared state instead of leaving
  // the tabs disconnected. Set by main.tsx → Workspace.
  onReconnectResume: (() => void) | null = null;

  get clientId(): string {
    return this._clientId;
  }

  // This client's conversation id. The IDE drives a single conversation, which
  // the server keys 1:1 with clientId (see Conversation.conversationId), so the
  // id we subscribe under matches the conversationId stamped on progress frames.
  // Surfaced as its own accessor so call sites read as conversation-centric for
  // when multiple conversations per client land.
  get conversationId(): string {
    return this._clientId;
  }

  // Subscribe a sink for a conversation's streamed progress / answer frames.
  // Call once when the conversation UI mounts; the returned function
  // unsubscribes (call on unmount). The subscription is PERSISTENT —
  // it is set up once and is NOT registered/torn-down per prompt. Replaces any
  // existing sink registered for the same conversationId.
  subscribeConversation = (
    conversationId: string,
    sink: ProgressCallback
  ): (() => void) => {
    this.conversationSinks.set(conversationId, sink);
    return () => {
      // Only remove if it's still our sink — a later subscribe for the same
      // conversation must not be clobbered by an earlier unsubscribe.
      if (this.conversationSinks.get(conversationId) === sink) {
        this.conversationSinks.delete(conversationId);
      }
    };
  };

  // The authenticated user's email, extracted from the JWT token payload.
  // Null when auth is disabled (no token) or token is malformed.
  readonly userEmail: string | null;

  private constructor(readonly args: AlphaClientArgs) {
    this._clientId = this.generateClientId();
    this.userEmail = args.userEmail ?? null;
    // Stamp identity onto every Context produced by the IDE helpers so
    // downstream logs in this tab carry the right user/session labels.
    setSessionId(this._clientId);
    setUserEmail(this.userEmail ?? undefined);
  }

  // Static async factory - waits for server and registers client
  static async create(args: AlphaClientArgs): Promise<Result<AlphaClient>> {
    const client = new AlphaClient(args);

    // Wait for AlphaServer to be ready
    const serverReadyResult = await client.waitForServer();
    if (serverReadyResult.err) {
      return Result.Err({
        why: "alpha-server-not-ready",
        data: serverReadyResult.val,
      });
    }

    // Register this client with the server
    const registerResult = await client.registerClient();
    if (registerResult.err) {
      return Result.Err({
        why: "failed-to-register-client",
        data: registerResult.val,
      });
    }

    // Install document/window listeners (visibilitychange, online) that
    // probe the WebSocket on transitions — catches zombie connections
    // after machine sleep / network blips.
    client.setupConnectivityListeners();

    // Connect WebSocket for progress streaming
    client.connectWebSocket();

    return Result.Ok(client);
  }

  // Wait for AlphaServer health endpoint to respond
  private async waitForServer(): AResult<void> {
    const { alphaServerUrl } = this.args;
    const healthUrl = `${alphaServerUrl}/health`;
    const maxAttempts = 30;
    const delayMs = 1000;

    console.log(`Waiting for AlphaServer at ${healthUrl}...`);

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await fetch(healthUrl);
        if (response.ok) {
          console.log(`AlphaServer ready after ${attempt} attempt(s)`);
          return Result.Ok(undefined);
        }
      } catch {
        // Connection refused - server not ready yet
      }

      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    return Result.Err({
      why: "server-timeout",
      data: `AlphaServer at ${healthUrl} did not respond after ${maxAttempts} attempts`,
    });
  }

  // Generate a unique client ID for this browser session
  private generateClientId(): string {
    const sessionId = crypto.randomUUID().substring(0, 8);
    return `ide-${sessionId}`;
  }

  // Register this client with AlphaServer
  private registerClient = async (): AResult<unknown> => {
    const ctx = createRequestContext();
    return this.call(ctx, {
      kind: "register-client",
      data: {
        clientId: this.clientId,
        userId: this.clientId, // In browser, userId = clientId for now
      },
    });
  };

  // Call AlphaServer with a typed Payload. Returns raw response.
  // Context is required and passed first (Go-style).
  // Optional timeoutMs for long-running operations (default: 10s).
  request = async (
    ctx: Context,
    payload: Payload,
    timeoutMs?: number
  ): AResult<unknown> => {
    const options = timeoutMs
      ? { ...DEFAULT_CALL_OPTIONS, timeoutMs }
      : DEFAULT_CALL_OPTIONS;
    return this.call(ctx, payload, options);
  };

  // Open a layout hierarchy in one request.
  // Returns all layouts in the hierarchy to minimize HTTP round-trips.
  openHierarchy = async (
    ctx: Context,
    ident: schema.Ident
  ): AResult<OpenHierarchyResult> => {
    // 120s timeout: large hierarchies (500+ layouts) require significant time
    // to fetch all layouts from GCS in parallel batches.
    const result = await this.call(
      ctx,
      { kind: "open-hierarchy", data: { kind: "ident", ident } },
      { maxAttempts: 1, timeoutMs: 120_000, retryDelayMs: 0 }
    );
    if (result.err) return result;

    // Validate response structure
    const validated = OpenHierarchyResult.validator.validate(result.val);
    if (validated.err) {
      return Result.Err({ why: "invalid-response", data: validated.val });
    }
    return Result.Ok(validated.val);
  };

  // Send a prompt to AlphaServer's Copilot endpoint.
  // Returns the (empty) ack when the HTTP request completes. Progress and the
  // streamed answer arrive separately over the WebSocket and are rendered via
  // the persistent per-conversation subscription (subscribeConversation), NOT
  // through this call. prompt() does not register a per-prompt sink.
  // The caller's `req.requestId` allows tracking and cancellation.
  prompt = async (ctx: Context, req: Prompt): AResult<unknown> => {
    const { requestId } = req;
    const reqId = requestId ?? crypto.randomUUID();

    // Create abort controller for this request (used by cancelPrompt)
    const controller = new AbortController();
    this.abortControllers.set(reqId, controller);

    try {
      // prompt() is non-blocking server-side: the ack returns promptly with an
      // empty body and the answer arrives later over the WebSocket stream
      // (routed to the conversation's sink by conversationId). The long timeout
      // still guards the (short) HTTP round-trip.
      return await this.call(
        ctx,
        {
          kind: "prompt",
          data: { ...req, requestId: reqId },
        },
        { maxAttempts: 1, timeoutMs: 300_000, retryDelayMs: 0 },
        false,
        controller.signal
      );
    } finally {
      // The HTTP request is done, so drop its abort controller. The conversation
      // progress subscription is independent of this call (it's persistent)
      // — nothing to tear down here.
      this.abortControllers.delete(reqId);
    }
  };

  // Cancel an in-flight prompt request.
  // Aborts the local fetch and notifies the server to stop processing.
  cancelPrompt = async (ctx: Context, requestId: string): AResult<void> => {
    // Abort the local fetch if still in flight
    const controller = this.abortControllers.get(requestId);
    if (controller) {
      controller.abort("User cancelled");
      this.abortControllers.delete(requestId);
    }

    // The conversation's progress subscription is persistent and spans
    // multiple prompts, so we do NOT tear it down here — cancelling a prompt
    // stops the run server-side (below); the subscription stays live for the
    // next prompt in the same conversation.

    // Notify server to stop processing (best-effort, fire-and-forget)
    // Use short timeout since we don't want to block on this
    const result = await this.call(
      ctx,
      { kind: "stop", data: { requestId } },
      { maxAttempts: 1, timeoutMs: 5_000, retryDelayMs: 0 }
    );

    if (result.err) {
      // Log but don't fail - the local abort already stopped the request
      console.warn("Failed to notify server of cancellation:", result.val);
    }

    return Result.Ok(undefined);
  };

  // Pull the current running-agent hierarchy for this conversation. Used on
  // panel open / tab-focus / reconnect; live updates arrive via onAgentTree.
  // Response is `AgentTreeNode | null` (null = no live conversation/tree).
  getAgentTree = async (ctx: Context): AResult<unknown> => {
    return this.call(ctx, { kind: "get-agent-tree", data: {} });
  };

  // Stop one agent AND every agent beneath it (registry-direct, scoped to this
  // conversation's tree).
  stopAgent = async (ctx: Context, agentId: string): AResult<unknown> => {
    return this.call(ctx, { kind: "stop-agent", data: { agentId } });
  };

  // Retire (evict) a parked agent subtree. Server refuses the root or a subtree
  // that still has an active agent.
  retireAgent = async (ctx: Context, agentId: string): AResult<unknown> => {
    return this.call(ctx, { kind: "retire-agent", data: { agentId } });
  };

  // Message one agent directly. Distinct from `prompt` (which addresses the
  // root); parent agents do NOT see this message in their context.
  messageAgent = async (
    ctx: Context,
    agentId: string,
    prompt: string
  ): AResult<unknown> => {
    return this.call(ctx, { kind: "message-agent", data: { agentId, prompt } });
  };

  // Handle a canvas capture request from AlphaServer.
  // Captures the active editor's canvas and sends the response back via WebSocket.
  // NOTE: This captures the *active* editor only. If the user switches tabs while
  // a prompt is in flight, the capture may not match the design Copilot is working on.
  private handleCaptureCanvasRequest(req: CaptureCanvasRequest): void {
    const { uuid } = req;

    if (!this.ws || !this.onCaptureCanvas) {
      return;
    }

    const result = this.onCaptureCanvas();
    const base = {
      kind: "capture-canvas-response",
      uuid,
      context: req.context,
      clientId: this.clientId,
    };
    const response = result.ok
      ? { ...base, success: true, imageBase64: result.val }
      : { ...base, success: false, error: result.val };

    this.ws.send(JSON.stringify(response));
  }

  // Call AlphaServer with a request
  // Call AlphaServer with a request.
  // Supports per-call timeout (via AbortController) and optional retries.
  // If server returns "unknown-client", automatically re-registers and retries once.
  // Optional externalSignal allows caller to abort the request (e.g., for user cancellation).
  private call = async (
    ctx: Context,
    req: Payload,
    options: CallOptions = DEFAULT_CALL_OPTIONS,
    isRetryAfterReregister: boolean = false,
    externalSignal?: AbortSignal
  ): AResult<unknown> => {
    // Log API call with GCP URL for debugging (filtered to skip noisy APIs)
    logWithGcpUrlFiltered("IDE", req.kind, ctx);

    const reqWithContext = {
      ...req,
      clientId: this.clientId,
      context: ctx.toWire(),
    };
    const body = JSON.stringify(reqWithContext);
    const { alphaServerUrl, getToken } = this.args;

    // Build headers with optional Authorization for authenticated requests
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (getToken) {
      const token = await getToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    let lastError: Result<unknown> = Result.Err({ why: "no-attempts" });

    for (let attempt = 0; attempt < options.maxAttempts; attempt++) {
      const controller = new AbortController();
      const timeoutSec = Math.round(options.timeoutMs / 1000);
      const timer = setTimeout(
        () =>
          controller.abort(
            `Request timed out after ${timeoutSec}s. The server may still be processing.`
          ),
        options.timeoutMs
      );

      // If external signal is provided, abort local controller when it fires
      const onExternalAbort = () => controller.abort("User cancelled");
      if (externalSignal) {
        externalSignal.addEventListener("abort", onExternalAbort);
      }

      const fetchResult = await f(alphaServerUrl, {
        method: "POST",
        body,
        headers,
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (externalSignal) {
        externalSignal.removeEventListener("abort", onExternalAbort);
      }

      if (fetchResult.err) {
        // Check if this was a user-initiated abort
        const errorStr = String(fetchResult.val);
        if (errorStr.includes("User cancelled")) {
          return Result.Err({ why: "cancelled-by-user" });
        }
        lastError = Result.Err({ why: errorStr });
      } else if (fetchResult.val.status === 401) {
        // Authentication required - return error so caller can handle it.
        // Don't reload: if Firebase isn't configured on the client, reloading
        // would cause an infinite loop since no login screen would appear.
        return Result.Err({ why: "auth-required" });
      } else if (fetchResult.val.status !== 200) {
        // Parse error response to check for recoverable errors
        const errorBody = await this.parseErrorResponse(fetchResult.val);

        // Handle unknown-client error by re-registering and retrying
        if (
          errorBody?.why === "unknown-client" &&
          !isRetryAfterReregister &&
          req.kind !== "register-client"
        ) {
          console.log(
            "Server returned unknown-client error, re-registering..."
          );
          const reregisterResult = await this.registerClient();
          if (reregisterResult.ok) {
            console.log(
              "Re-registration successful, retrying original request"
            );
            // Retry the original request with flag to prevent infinite loops
            return this.call(ctx, req, options, true);
          } else {
            console.error("Re-registration failed:", reregisterResult.val);
            return Result.Err({
              why: "reregistration-failed",
              data: reregisterResult.val,
            });
          }
        }

        lastError = Result.Err({
          why: errorBody?.why ?? "failure-response",
          data: errorBody?.data ?? fetchResult.val.status,
        });
      } else {
        return Ok(await fetchResult.val.json());
      }

      // Wait before retrying (skip delay on last attempt)
      if (attempt < options.maxAttempts - 1 && options.retryDelayMs > 0) {
        await new Promise((resolve) =>
          setTimeout(resolve, options.retryDelayMs)
        );
      }
    }

    return lastError;
  };

  // Parse error response body to extract structured error info.
  // Returns null if parsing fails (non-JSON response, etc.)
  private async parseErrorResponse(
    response: Response
  ): Promise<{ why: string; data?: unknown } | null> {
    try {
      const body = (await response.json()) as unknown;
      if (
        typeof body === "object" &&
        body !== null &&
        "why" in body &&
        typeof (body as Record<string, unknown>).why === "string"
      ) {
        return body as { why: string; data?: unknown };
      }
      return null;
    } catch {
      // Non-JSON response or parsing error
      return null;
    }
  }

  // ===== WebSocket Methods =====

  // Connect to WebSocket for progress streaming
  private connectWebSocket(): void {
    if (this.wsDisposed) {
      return;
    }

    // Derive WebSocket URL from HTTP URL (same port, /ws path)
    const { alphaServerUrl } = this.args;
    const wsUrl =
      alphaServerUrl.replace(/^http:/, "ws:").replace(/^https:/, "wss:") +
      "/ws";

    console.log(
      `WebSocket: Connecting to ${wsUrl} (attempt ${
        this.wsReconnectManager.getAttempts() + 1
      })`
    );

    try {
      this.ws = new WebSocket(wsUrl);
    } catch (error) {
      console.error("WebSocket: Failed to create WebSocket:", error);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      console.log(`WebSocket: Connected to ${wsUrl}`);
      // Snapshot state we need BEFORE resetting the reconnect manager, because
      // reset() zeroes the attempt counter and starts a fresh connection-start
      // timestamp for the stable-connection heuristic.
      const attemptsUsed = this.wsReconnectManager.getAttempts();
      const downtimeMs =
        this.wsLastDisconnectedAt !== null
          ? Date.now() - this.wsLastDisconnectedAt
          : 0;
      const wasReconnect = this.wsEverConnected;

      this.wsReconnecting = false;
      this.wsReconnectManager.reset();
      this.wsLastDisconnectedAt = null;

      // Register this client's WebSocket with the server
      const registerMessage = JSON.stringify({
        kind: "register-websocket",
        clientId: this.clientId,
      });
      this.ws?.send(registerMessage);
      console.log(
        `WebSocket: Sent registration for clientId: ${this.clientId}`
      );

      // Start the app-level ping loop. The browser WS API hides protocol-level
      // pings so we rely on this to detect zombie connections (e.g. after
      // machine sleep). See PING_INTERVAL_MS / PONG_TIMEOUT_MS above.
      this.startHeartbeat();

      this.onConnectionStateChanged?.(true);
      if (wasReconnect) {
        this.onConnectionEvent?.({
          kind: "reconnected",
          clientId: this.clientId,
          url: wsUrl,
          downtimeMs,
          attempts: attemptsUsed,
        });
      } else {
        this.wsEverConnected = true;
        this.onConnectionEvent?.({
          kind: "connected",
          clientId: this.clientId,
          url: wsUrl,
        });
      }
    };

    this.ws.onmessage = (event: MessageEvent) => {
      // Parse JSON with error handling
      let data: unknown;
      try {
        data = JSON.parse(event.data as string) as unknown;
      } catch (error) {
        console.error("WebSocket: Error parsing message:", error);
        return;
      }

      // Check for pong first — it's the simplest discriminator (zero-payload
      // message) and the most common non-noise message we receive (one per
      // ~20s heartbeat), so dispatching it before the heavier typia
      // validators avoids unnecessary work. Pong clears any pending
      // pong-deadline timer; missed pongs are what drive forceReconnect().
      const pong = PongMessageValidator.validator.validate(data);
      if (pong.ok) {
        if (this.pongTimeout) {
          clearTimeout(this.pongTimeout);
          this.pongTimeout = null;
        }
        return;
      }

      // Register-ack carries the serverInstanceId. On the first ack we
      // remember it; on subsequent acks (after a reconnect) a mismatch means
      // the server restarted and our session is invalid, and clientKnown
      // false means the server doesn't have a ClientContext for us.
      const ackValidation =
        RegisterWebSocketAckValidator.validator.validate(data);
      if (ackValidation.ok) {
        this.handleRegisterAck(ackValidation.val);
        return;
      }

      // Check for progress messages (streaming during prompt). Route to the
      // matching conversation's sink by conversationId.
      const progress = ProgressMessageValidator.validator.validate(data);
      if (progress.ok) {
        const sink = this.conversationSinks.get(progress.val.conversationId);
        if (sink) {
          sink(progress.val.message, progress.val.kind);
        }
        return;
      }

      // Check for agent-tree pushes (running-agent hierarchy changed)
      const agentTree = AgentTreeMessageValidator.validator.validate(data);
      if (agentTree.ok && this.onAgentTree) {
        this.onAgentTree(agentTree.val);
        return;
      }

      // Check for design-open events (LM opened a design)
      const designOpen = DesignOpenMessageValidator.validator.validate(data);
      if (designOpen.ok && this.onDesignOpen) {
        this.onDesignOpen(designOpen.val);
        return;
      }

      // Check for incremental design-changes events (state changes streamed
      // live). Checked before design-load: both carry an `ident`, but this
      // one is discriminated by `kind:"changes"` + a `changes` array.
      const designChanges =
        DesignChangesMessageValidator.validator.validate(data);
      if (designChanges.ok && this.onDesignChanges) {
        this.onDesignChanges(designChanges.val);
        return;
      }

      // Check for design-load events (state changes applied)
      const designLoad = DesignLoadMessageValidator.validator.validate(data);
      if (designLoad.ok && this.onDesignLoad) {
        this.onDesignLoad(designLoad.val);
        return;
      }

      // Check for LVS result events (LVS verification completed)
      const lvsResult = LvsResultMessageValidator.validator.validate(data);
      if (lvsResult.ok && this.onLvsResult) {
        this.onLvsResult(lvsResult.val);
        return;
      }

      // Check for DRC result events (DRC verification completed)
      const drcResult = DrcResultMessageValidator.validator.validate(data);
      if (drcResult.ok && this.onDrcResult) {
        this.onDrcResult(drcResult.val);
        return;
      }

      // Check for IVA result events (IVA verification completed)
      const ivaResult = IvaResultMessageValidator.validator.validate(data);
      if (ivaResult.ok && this.onIvaResult) {
        this.onIvaResult(ivaResult.val);
        return;
      }

      // Check for SRV result events (Schematic Rules Validation completed)
      const srvResult = SrvResultMessageValidator.validator.validate(data);
      if (srvResult.ok && this.onSrvResult) {
        this.onSrvResult(srvResult.val);
        return;
      }

      // Check for canvas capture requests (get-img tool)
      const captureReq = CaptureCanvasRequestValidator.validator.validate(data);
      if (captureReq.ok) {
        this.handleCaptureCanvasRequest(captureReq.val);
        return;
      }

      // Check for depot tree change events (design saved/added/removed).
      // These share a common shape with a `kind` field.
      const msg = data as Record<string, unknown>;
      if (
        msg.kind === "design-saved" ||
        msg.kind === "design-added" ||
        msg.kind === "design-removed"
      ) {
        console.log(`WebSocket: Depot tree changed (${msg.kind as string})`);
        if (this.onDesignTreeChanged) {
          this.onDesignTreeChanged(
            msg.kind as "design-saved" | "design-added" | "design-removed"
          );
        }
        return;
      }

      // Unknown message type - log for debugging
      console.log("WebSocket: Unknown message type:", data);
    };

    this.ws.onclose = (event: CloseEvent) => {
      const { durationMs, wasStable } =
        this.wsReconnectManager.recordConnectionEnd();
      const connectionDuration = ReconnectionManager.formatDuration(durationMs);

      console.log(
        `WebSocket: Connection closed (code: ${event.code}, reason: ${
          event.reason || "none"
        }, duration: ${connectionDuration})`
      );

      if (wasStable) {
        console.log(
          `WebSocket: Connection was stable (${connectionDuration}), resetting reconnection attempts`
        );
      }

      // Tear down heartbeat so we don't fire pings into a dead socket.
      this.stopHeartbeat();
      // Record the disconnect time so the next onopen can compute downtime.
      // Only set it if we had previously connected — pre-first-open closes
      // (e.g. immediate handshake failures) aren't reconnects.
      if (this.wsEverConnected) {
        this.wsLastDisconnectedAt = Date.now();
      }

      this.onConnectionEvent?.({
        kind: "disconnected",
        clientId: this.clientId,
        code: event.code,
        reason: event.reason || "",
        wasStable,
        durationMs,
      });

      this.onConnectionStateChanged?.(false);
      this.scheduleReconnect();
    };

    this.ws.onerror = (event: Event) => {
      console.error("WebSocket: Error:", event);
      // onclose will be called after onerror, so reconnection is handled there
    };
  }

  // Schedule WebSocket reconnection
  private scheduleReconnect(): void {
    if (
      this.wsDisposed ||
      this.wsReconnecting ||
      this.wsReconnectTimeout ||
      this.connectionLost
    ) {
      return;
    }

    // Cap the number of attempts. Once exhausted, escalate to onConnectionLost
    // so the UI can prompt the user — silent retry forever just leaves them
    // staring at a stale page.
    if (
      this.wsReconnectManager.getAttempts() >=
      AlphaClient.MAX_RECONNECT_ATTEMPTS_BEFORE_DIALOG
    ) {
      console.warn(
        `WebSocket: reconnect attempts exhausted (${this.wsReconnectManager.getAttempts()}), giving up`
      );
      this.fireConnectionLost("retries-exhausted");
      return;
    }

    const delay = this.wsReconnectManager.getNextDelay();
    console.log(
      `WebSocket: Scheduling reconnection in ${delay / 1000}s (attempt ${
        this.wsReconnectManager.getAttempts() + 1
      })`
    );

    this.wsReconnecting = true;
    this.wsReconnectManager.recordAttempt();

    this.wsReconnectTimeout = setTimeout(() => {
      this.wsReconnectTimeout = null;
      if (!this.wsDisposed) {
        this.wsReconnecting = false;
        this.connectWebSocket();
      }
    }, delay);
  }

  // Handle a register-websocket-ack message. The first ack establishes our
  // baseline server identity; subsequent acks (on reconnect) must match it.
  private handleRegisterAck(ack: RegisterWebSocketAck): void {
    console.log(
      `WebSocket: Received register-ack (serverInstanceId=${
        ack.serverInstanceId
      }, clientKnown=${String(ack.clientKnown)})`
    );

    if (this.knownServerInstanceId === null) {
      // First successful registration this session — remember the identity.
      this.knownServerInstanceId = ack.serverInstanceId;
      // clientKnown should always be true on first registration since the
      // HTTP register-client call has already succeeded by this point. If
      // somehow false, escalate so the user sees something useful instead of
      // silent breakage.
      if (!ack.clientKnown) {
        console.warn(
          "WebSocket: initial register-ack reports clientKnown=false; treating as client-unknown"
        );
        this.fireConnectionLost("client-unknown");
      }
      return;
    }

    if (ack.serverInstanceId !== this.knownServerInstanceId) {
      console.warn(
        `WebSocket: server restarted (was ${this.knownServerInstanceId}, now ${ack.serverInstanceId})`
      );
      this.fireConnectionLost("server-restarted");
      return;
    }

    if (!ack.clientKnown) {
      console.warn(
        "WebSocket: server is the same instance but no longer knows our clientId; recovering in place"
      );
      // The server dropped our ClientContext on the disconnect, but it's the
      // SAME instance — the shared design state is still alive in its grace
      // window. Recover silently (re-register + re-open) instead of forcing a
      // page reload, so unsaved in-memory edits are preserved.
      void this.recoverAfterClientUnknown();
      return;
    }

    // Same server, still knows us — normal reconnect.
  }

  // Silent recovery from a same-instance "client-unknown" reconnect: rebuild
  // the ClientContext, re-associate this socket, then ask the app to re-open
  // its designs so the fresh server-side StateManager re-adopts the live
  // (grace-windowed) shared state. Falls back to the reload dialog if the
  // re-registration itself fails.
  private recoverAfterClientUnknown = async (): Promise<void> => {
    if (this.recovering || this.connectionLost) {
      return;
    }
    this.recovering = true;
    try {
      const reregister = await this.registerClient();
      if (reregister.err) {
        console.error(
          "WebSocket: re-registration during recovery failed:",
          reregister.val
        );
        this.fireConnectionLost("client-unknown");
        return;
      }
      // Re-associate this socket with the now-known clientId. If the socket
      // dropped again during recovery, bail rather than silently no-op the
      // send and re-open against a dead socket — the pending reconnect will
      // re-run this path once a new socket opens.
      if (!this.ws) {
        return;
      }
      this.ws.send(
        JSON.stringify({ kind: "register-websocket", clientId: this.clientId })
      );
      // Re-open the open designs so the server re-adopts the live shared state.
      this.onReconnectResume?.();
    } finally {
      this.recovering = false;
    }
  };

  // Fire onConnectionLost (at most once) and stop trying to reconnect.
  // We deliberately do not close the socket here: leaving it open lets the
  // user keep seeing the dialog and the reload button stays clickable; the
  // socket will be torn down on page reload.
  private fireConnectionLost(reason: ConnectionLostReason): void {
    if (this.connectionLost) {
      return;
    }
    this.connectionLost = true;
    if (this.wsReconnectTimeout) {
      window.clearTimeout(this.wsReconnectTimeout);
      this.wsReconnectTimeout = null;
    }
    this.wsReconnecting = false;
    this.onConnectionLost?.(reason);
  }

  // Start the app-level ping/pong heartbeat. Sends a JSON ping every
  // PING_INTERVAL_MS; if no pong arrives within PONG_TIMEOUT_MS, forces a
  // reconnect. Idempotent: safe to call repeatedly.
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.pingInterval = setInterval(() => {
      // Skip while a long op is in flight; liveness is covered by the op's
      // own request timeout.
      if (this.inFlightOps > 0) {
        return;
      }
      this.sendPing("heartbeat", AlphaClient.PONG_TIMEOUT_MS);
    }, AlphaClient.PING_INTERVAL_MS);
  }

  // Stop the heartbeat and clear any pending pong-deadline timer. Called on
  // socket close and from dispose(); must not throw.
  private stopHeartbeat(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  // Start of a long operation: suppress the heartbeat and clear any
  // already-armed pong deadline so it can't fire mid-op.
  beginBusy(): void {
    this.inFlightOps++;
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  // End of a long operation. On the last one, ping immediately to promptly
  // detect a socket that died during the op. Clamped at zero so an unbalanced
  // call can't wedge suppression on.
  endBusy(): void {
    if (this.inFlightOps > 0) {
      this.inFlightOps--;
    }
    if (this.inFlightOps === 0) {
      this.sendPing("post-op", AlphaClient.PONG_TIMEOUT_MS);
    }
  }

  // Send a single ping and (re)arm the pong-deadline timer. `reason` is
  // passed through to forceReconnect() on timeout for diagnostics.
  // If a pong-deadline is already armed (e.g. a prior ping is still in
  // flight), we leave it alone — whichever event happens first decides
  // the outcome: an incoming pong clears the timer (healthy), or the
  // timer expires and triggers forceReconnect() (dead socket). Re-arming
  // with a fresh deadline would only delay detection.
  private sendPing(reason: string, timeoutMs: number): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    try {
      this.ws.send(
        JSON.stringify({
          kind: "ping",
          clientId: this.clientId,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      console.warn("WebSocket: ping send failed:", error);
      this.forceReconnect(`ping-send-failed-${reason}`);
      return;
    }
    this.pongTimeout ??= setTimeout(() => {
      this.pongTimeout = null;
      console.warn(
        `WebSocket: pong deadline missed (reason=${reason}), forcing reconnect`
      );
      this.forceReconnect(`pong-timeout-${reason}`);
    }, timeoutMs);
  }

  // Close the current socket to trigger the standard reconnect path. Uses
  // a 4xxx application close code so the reason shows up in server logs
  // without colliding with WebSocket-protocol-defined codes. No-op if the
  // socket is already gone or we're shutting down.
  private forceReconnect(reason: string): void {
    if (this.wsDisposed) {
      return;
    }
    const ws = this.ws;
    if (!ws) {
      return;
    }
    console.warn(`WebSocket: force-reconnect reason=${reason}`);
    try {
      // Codes in the 4000-4999 range are reserved for application use.
      ws.close(4001, reason);
    } catch (error) {
      // close() can throw (e.g. invalid args, socket in bad state). When it
      // throws synchronously the socket never transitions to CLOSING, so
      // onclose never fires — meaning the normal scheduleReconnect() path
      // is skipped. Drive the recovery manually instead of leaving the
      // client stuck holding a dead handle.
      console.warn("WebSocket: close() during force-reconnect threw:", error);
      this.ws = null;
      this.stopHeartbeat();
      this.onConnectionStateChanged?.(false);
      this.scheduleReconnect();
    }
  }

  // Attach document/window listeners that expedite zombie detection:
  //   - visibilitychange (tab becomes visible after sleep)
  //   - online           (browser regains network)
  // Both fire a probe ping with a short deadline; if no pong arrives we
  // force-reconnect. Idempotent — only attaches once per AlphaClient instance.
  private setupConnectivityListeners(): void {
    if (this.connectivityListenersAttached) {
      return;
    }
    this.connectivityListenersAttached = true;
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          this.probeConnection("visibility-visible");
        }
      });
    }
    if (typeof window !== "undefined") {
      window.addEventListener("online", () => {
        this.probeConnection("online");
      });
    }
  }

  // Probe whether the current WebSocket is actually alive. Called on
  // visibilitychange/online events where we have a strong signal that the
  // network state just changed (and the socket may have died silently).
  //
  // Behaviour:
  //   - disposed: no-op
  //   - not open: if a reconnect is already scheduled, expedite it
  //   - open: send a ping with a short (PROBE_PONG_TIMEOUT_MS) deadline
  private probeConnection(reason: string): void {
    if (this.wsDisposed) {
      return;
    }
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      if (this.wsReconnecting && this.wsReconnectTimeout) {
        console.log(`WebSocket: ${reason} — expediting pending reconnect`);
        clearTimeout(this.wsReconnectTimeout);
        this.wsReconnectTimeout = null;
        this.wsReconnecting = false;
        this.connectWebSocket();
      }
      return;
    }
    console.log(`WebSocket: ${reason} — sending probe ping`);
    // Clear any running pong-deadline so our short probe deadline wins.
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
    this.sendPing(`probe-${reason}`, AlphaClient.PROBE_PONG_TIMEOUT_MS);
  }

  // Clean up WebSocket and resources
  dispose(): void {
    console.log("AlphaClient: Disposing");
    this.wsDisposed = true;
    this.wsReconnecting = false;

    if (this.wsReconnectTimeout) {
      clearTimeout(this.wsReconnectTimeout);
      this.wsReconnectTimeout = null;
    }

    this.stopHeartbeat();

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close();
      }
      this.ws = null;
    }

    this.conversationSinks.clear();
    this.onDesignLoad = null;
    this.onDesignChanges = null;
    this.onDesignTreeChanged = null;
    this.onAgentTree = null;
  }
}
